import type { ScheduleBlock } from "@/lib/api";

const UUID_RE = /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi;
const LONG_ID_RE = /\b\d{10,}\b/g;
// Keep short mixed slugs that can still be real user-facing labels, but strip long machine-like tokens.
const MIXED_ID_RE = /\b(?=[a-z0-9]*[a-z])(?=(?:[a-z0-9]*\d){2,})[a-z0-9]{8,}\b/gi;
// Strip trailing short random ID suffixes (5-6 mixed alphanumeric with at least one digit) at end of title.
// Catches: "Email Alex about the budget 50wlmu", "Smaller restart 5abyhm"
// Skips real words like "budget" (no digits) via the digit requirement.
const TRAILING_SHORT_ID_RE = / (?=[a-z0-9]*\d)[a-z0-9]{5,6}$/i;
const EXACT_REWRITE_RULES: Array<{ pattern: RegExp; replacement: string }> = [
  { pattern: /^manual completion check(?:\b.*)?$/i, replacement: "Progress check" },
  { pattern: /^undo completion check(?:\b.*)?$/i, replacement: "Progress check" },
  { pattern: /^carry forward smaller(?:\b.*)?$/i, replacement: "Smaller step note" },
  {
    pattern: /^resume(?:[-_\s]+)do(?:[-_\s]+)next(?:[-_\s]+[a-z0-9]{4,12})?$/i,
    replacement: "Resume next step",
  },
  {
    pattern: /^do(?:[-_\s]+)next(?:[-_\s]+[a-z0-9]{4,12})?$/i,
    replacement: "Next step",
  },
  {
    pattern: /^keep(?:[-_\s]+)open(?:[-_\s]+[a-z0-9]{4,12})?$/i,
    replacement: "Keep open",
  },
  {
    pattern: /^revisit(?:[-_\s]+later)?(?:[-_\s]+[a-z0-9]{4,12})?$/i,
    replacement: "Revisit later",
  },
  {
    pattern: /^cross(?:[-_\s]+)surface(?:[-_\s]+)article(?:[-_\s]+[a-z0-9]{4,12})?(?:[-_\s]+goal)$/i,
    replacement: "Saved article for your goal",
  },
  {
    pattern: /^cross(?:[-_\s]+)surface(?:[-_\s]+)article(?:[-_\s]+[a-z0-9]{4,12})?$/i,
    replacement: "Saved article",
  },
  {
    pattern: /^recovery(?:[-_\s]+)target(?:[-_\s]+[a-z0-9]{4,12})?(?:[-_\s]+goal)?$/i,
    replacement: "Recovery target",
  },
  { pattern: /^queue placement article(?:\b.*goal)?$/i, replacement: "Saved article for your goal" },
  { pattern: /^queue placement article(?:\b.*)?$/i, replacement: "Saved article" },
  { pattern: /^restart task(?:\b.*)?$/i, replacement: "Restart step" },
];
const INLINE_REWRITE_RULES: Array<{ pattern: RegExp; replacement: string }> = [
  {
    pattern: /\bresume(?:[-_\s]+)do(?:[-_\s]+)next(?:[-_\s]+(?=[a-z0-9]*\d)[a-z0-9]{4,12})\b/gi,
    replacement: "resume next step",
  },
  {
    pattern: /\bdo(?:[-_\s]+)next(?:[-_\s]+(?=[a-z0-9]*\d)[a-z0-9]{4,12})\b/gi,
    replacement: "next step",
  },
  {
    pattern: /\bkeep(?:[-_\s]+)open(?:[-_\s]+(?=[a-z0-9]*\d)[a-z0-9]{4,12})\b/gi,
    replacement: "this thread",
  },
  {
    pattern: /\brevisit(?:[-_\s]+(?=[a-z0-9]*\d)[a-z0-9]{4,12})\b/gi,
    replacement: "current",
  },
  {
    pattern: /\bcross(?:[-_\s]+)surface(?:[-_\s]+)article(?:[-_\s]+(?=[a-z0-9]*\d)[a-z0-9]{4,12})(?:[-_\s]+goal)\b/gi,
    replacement: "saved article for your goal",
  },
  {
    pattern: /\bcross(?:[-_\s]+)surface(?:[-_\s]+)article(?:[-_\s]+(?=[a-z0-9]*\d)[a-z0-9]{4,12})\b/gi,
    replacement: "saved article",
  },
  {
    pattern: /\brecovery(?:[-_\s]+)target(?:[-_\s]+(?=[a-z0-9]*\d)[a-z0-9]{4,12})\b/gi,
    replacement: "recovery target",
  },
];

function compactLabel(value: string | null | undefined, maxChars: number): string {
  if (typeof value !== "string") return "";
  const normalized = value.replace(/\s+/g, " ").trim();
  if (!normalized) return "";
  if (normalized.length <= maxChars) return normalized;
  const clipped = normalized.slice(0, maxChars).split(" ");
  clipped.pop();
  return `${(clipped.join(" ").trim() || normalized.slice(0, maxChars).trim()).replace(/[ ,.;:]+$/, "")}…`;
}

function savedResourceVerb(contentType?: string | null): "Read" | "Watch" | "Listen" | "Continue" {
  if (contentType === "video") return "Watch";
  if (contentType === "podcast") return "Listen";
  if (contentType === "course") return "Continue";
  return "Read";
}

function extractExistingVerb(title: string): { verb: "Read" | "Watch" | "Listen" | "Continue"; body: string } {
  const patterns: Array<{ regex: RegExp; verb: "Read" | "Watch" | "Listen" | "Continue" }> = [
    { regex: /^Review\s+/i, verb: "Read" },
    { regex: /^Read:\s*/i, verb: "Read" },
    { regex: /^Watch:\s*/i, verb: "Watch" },
    { regex: /^Watch\s+/i, verb: "Watch" },
    { regex: /^Listen:\s*/i, verb: "Listen" },
    { regex: /^Listen to\s+/i, verb: "Listen" },
    { regex: /^Continue:\s*/i, verb: "Continue" },
    { regex: /^Continue\s+/i, verb: "Continue" },
  ];

  let current = title.trim();
  let detectedVerb: "Read" | "Watch" | "Listen" | "Continue" | null = null;

  while (current) {
    const match = patterns.find((pattern) => pattern.regex.test(current));
    if (!match) break;
    detectedVerb = detectedVerb ?? match.verb;
    current = current.replace(match.regex, "").trim();
  }

  if (detectedVerb) {
    return { verb: detectedVerb, body: current };
  }

  for (const pattern of patterns) {
    if (pattern.regex.test(title)) {
      return { verb: pattern.verb, body: title.replace(pattern.regex, "").trim() };
    }
  }

  return { verb: "Read", body: title };
}

export function normalizeSavedResourceTitle(title: string | null | undefined): string {
  return sanitizeSurfaceTitle(title)
    .replace(/^[^A-Za-z0-9]+/, "")
    .replace(/^["'“”]+|["'“”]+$/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

export function compactSurfaceTitle(title: string | null | undefined, maxChars: number = 52): string {
  return compactLabel(sanitizeSurfaceTitle(title), maxChars);
}

export function sanitizeSurfaceTitle(title: string | null | undefined): string {
  if (typeof title !== "string") return "";
  const normalized = title
    .replace(UUID_RE, " ")
    .replace(LONG_ID_RE, " ")
    .replace(MIXED_ID_RE, " ")
    .replace(TRAILING_SHORT_ID_RE, "")
    .replace(/\s+/g, " ")
    .trim();
  const lowered = normalized.toLowerCase();

  if (lowered.startsWith("carry forward later") || lowered.startsWith("carry forward tomorrow")) {
    return "Carry-forward note";
  }

  for (const rule of EXACT_REWRITE_RULES) {
    if (rule.pattern.test(normalized)) {
      return rule.replacement;
    }
  }

  let rewritten = normalized;
  for (const rule of INLINE_REWRITE_RULES) {
    rewritten = rewritten.replace(rule.pattern, rule.replacement);
  }
  rewritten = rewritten.replace(/\s+/g, " ").trim();

  if (!rewritten) return normalized;
  if (/^[A-Z]/.test(normalized) && /^[a-z]/.test(rewritten)) {
    return `${rewritten[0].toUpperCase()}${rewritten.slice(1)}`;
  }
  return rewritten;
}

function inferGoalCue(goalTitle: string | null | undefined, title: string): string | null {
  if (goalTitle?.trim()) return goalTitle.trim();
  const progressMatch = title.match(/progress on ['"]([^'"]+)['"]/i);
  if (progressMatch?.[1]) return progressMatch[1].trim();
  return null;
}

function looksLikeLegacySavedResourceTitle(title: string): boolean {
  const normalized = title.trim().toLowerCase();
  return (
    normalized.startsWith("review ") ||
    normalized.startsWith("read ") ||
    normalized.startsWith("watch ") ||
    normalized.startsWith("listen ") ||
    normalized.startsWith("continue ") ||
    normalized.includes("queue placement article") ||
    normalized.includes("to kickstart your progress on")
  );
}

function collapseRepeatedDisplayVerb(value: string): string {
  return value
    .replace(/^Read:\s+Read:\s+/i, "Read: ")
    .replace(/^Read:\s+Read\s+/i, "Read ")
    .replace(/^Read:\s+for\s+/i, "Read for ")
    .replace(/^Watch:\s+Watch:\s+/i, "Watch: ")
    .replace(/^Watch:\s+Watch\s+/i, "Watch ")
    .replace(/^Listen:\s+Listen:\s+/i, "Listen: ")
    .replace(/^Listen:\s+Listen(?: to)?\s+/i, "Listen ")
    .replace(/^Continue:\s+Continue:\s+/i, "Continue: ")
    .replace(/^Continue:\s+Continue\s+/i, "Continue ")
    .trim();
}

export function buildSavedResourceTaskTitle(contentType: string | null | undefined, rawTitle: string): string {
  const verb = savedResourceVerb(contentType);
  const normalizedTitle = normalizeSavedResourceTitle(rawTitle);
  const compactTitle = compactLabel(normalizedTitle, 72);

  if (!compactTitle) return "Review saved resource";
  if (verb === "Watch") return `Watch ${compactTitle}`;
  if (verb === "Listen") return `Listen to ${compactTitle}`;
  if (verb === "Continue") return `Continue ${compactTitle}`;
  return `Read ${compactTitle}`;
}

export function buildSavedResourceScheduleTitle(
  contentType: string | null | undefined,
  rawTitle: string,
  goalTitle?: string | null
): string {
  const normalizedTitle = normalizeSavedResourceTitle(rawTitle);
  const { verb: derivedVerb, body } = extractExistingVerb(normalizedTitle);
  const verb = contentType ? savedResourceVerb(contentType) : derivedVerb;
  const inferredGoal = inferGoalCue(goalTitle, normalizedTitle);
  const goalCue = inferredGoal ? compactLabel(inferredGoal, 34) : "";

  if (/^for\s+/i.test(body) && goalCue) {
    return collapseRepeatedDisplayVerb(`${verb} for ${goalCue}`);
  }

  if (body.length > 52 && goalCue) {
    return collapseRepeatedDisplayVerb(`${verb} for ${goalCue}`);
  }

  const compactBody = compactLabel(body, 42);
  if (!compactBody) {
    return collapseRepeatedDisplayVerb(goalCue ? `${verb} for ${goalCue}` : "Saved resource");
  }

  if (/^for\s+/i.test(compactBody)) {
    return collapseRepeatedDisplayVerb(`${verb} ${compactBody}`);
  }

  return collapseRepeatedDisplayVerb(`${verb}: ${compactBody}`);
}

export function displaySavedResourceBlockTitle(
  block: Pick<ScheduleBlock, "kind" | "title" | "goal_title" | "block_metadata">
): string {
  const normalizedTitle = normalizeSavedResourceTitle(block.title);
  const isQueueLike =
    block.block_metadata?.source === "queue" ||
    (block.kind === "custom" && looksLikeLegacySavedResourceTitle(normalizedTitle)) ||
    (block.kind === "task" && /to kickstart your progress on ['"]/i.test(normalizedTitle));

  if (!isQueueLike) {
    return sanitizeSurfaceTitle(block.title);
  }

  return buildSavedResourceScheduleTitle(
    typeof block.block_metadata?.content_type === "string" ? block.block_metadata.content_type : null,
    normalizedTitle,
    block.goal_title
  );
}
